/**
 * ProUpsellCard — inline, non-intrusive "Upgrade to PRO" card shown where PRO
 * features are gated (feed limits, archive depth, search caps).
 * Emits a PostHog `paywall_viewed {surface}` event once on render.
 *
 * KILL SWITCH (iter85): every upsell surface obeys the global web-billing
 * toggle (GET /api/billing/config → {enabled}). While billing is dark-deployed
 * (enabled:false) the card renders nothing and fires no analytics. The config
 * fetch is module-cached so N cards on a page cost a single request.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, ArrowRight } from "lucide-react";
import { API } from "../config";

let _billingCfgPromise = null;

function fetchBillingConfig() {
  if (!_billingCfgPromise) {
    _billingCfgPromise = axios
      .get(`${API}/billing/config`)
      .then(r => ({ enabled: !!r.data?.enabled, prices: r.data?.prices || null }))
      .catch(() => ({ enabled: false, prices: null })); // fail closed: no upsell if config unreachable
  }
  return _billingCfgPromise;
}

/** {enabled, prices} once known, null while loading. Shared, single fetch per page load. */
export function useBillingConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchBillingConfig().then(v => { if (alive) setCfg(v); });
    return () => { alive = false; };
  }, []);
  return cfg;
}

/** true / false once known, null while loading. Shared, single fetch per page load. */
export function useBillingEnabled() {
  const cfg = useBillingConfig();
  return cfg === null ? null : cfg.enabled;
}

export const ProUpsellCard = ({ surface, darkMode = false, message }) => {
  const dm = darkMode;
  const fired = useRef(false);
  const billingEnabled = useBillingEnabled();

  useEffect(() => {
    if (billingEnabled !== true || fired.current) return;
    fired.current = true;
    try { window.posthog?.capture("paywall_viewed", { surface }); } catch { /* analytics best-effort */ }
  }, [surface, billingEnabled]);

  if (billingEnabled !== true) return null;

  return (
    <div data-testid={`pro-upsell-${surface}`}
      className={`rounded-2xl p-5 flex items-center justify-between gap-4 ${dm ? "bg-cyan-400/5" : "bg-[#006874]/5"}`}
      style={{ boxShadow: dm ? "0 0 0 1px rgba(34,211,238,0.15)" : "0 0 0 1px rgba(0,104,116,0.15)" }}>
      <div className="flex items-start gap-3">
        <Sparkles className={`w-4 h-4 mt-0.5 shrink-0 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
        <div>
          <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>TourismIntel PRO</p>
          <p className={`text-xs mt-0.5 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
            {message || "Full archive depth, unlimited semantic search, themed digests & story clusters."}
          </p>
        </div>
      </div>
      <Link to="/pro" data-testid={`pro-upsell-${surface}-cta`}
        onClick={() => { try { window.posthog?.capture("paywall_clicked", { surface }); } catch { /* best-effort */ } }}
        className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full category-tag ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>
        Upgrade<ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
};

export default ProUpsellCard;
